import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { FactorStatusValue } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ConstructionTypeData, ContractSeasonData } from 'gql/graphql';
import { OtherCostCalculationTypeEnum, SectionEnum, SortEnumType, type ContractOtherCostItemData } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { Checkbox } from 'uikit';
import { formatNumberToRu, yesOrNoRepresentation } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { UnitCostWithOtherHeader } from '../../PriceList/PriceListUER/cellRenderers';
import { TYPE_CONST } from '../../WorkOrder/AddOrderOther/types';
import { CostOtherCellRendererComponent } from '../AgreementsAddOther/costOtherCellRendererComponent';
import {
    uerContractChangeOtherCostRowsForIncluding,
    uerOthersForIncludingToContractChangeCalculatingTypeFilter,
    uerOthersForIncludingToContractChangeNameFilter,
} from '../AgreementsQueries';

const TITLE = 'Добавление прочих затрат';

const sectionLabels = {
    [SectionEnum.CustomerLimits]: 'Лимиты заказчика',
    [SectionEnum.Others]: 'Прочие затраты 9 Главы',
    [SectionEnum.WorkOrderTotals]: 'Итоги НЗ',
};

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    return {
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (!value.length) {
                return acc;
            }

            if (value.length) {
                acc = [
                    ...acc,
                    {
                        [key]: { in: value },
                    },
                ];
            }

            return acc;
        }, []),
    };
};

export const getUpdateRowTable = (items: ContractOtherCostItemData[]) => {
    if (!items || items.length === 0) {
        return [];
    }

    const groupedBySection = R.groupBy((item) => item?.section ?? '', items);

    return Object.entries(groupedBySection).reduce<
        (ContractOtherCostItemData | { id: string; name: string; type: string })[]
    >((acc, [section, sectionItems]) => {
        const label = sectionLabels[section as Exclude<SectionEnum, SectionEnum.LimitedCost>] || '';

        return [...acc, { id: `section_${section}`, name: label, type: 'section' }, ...sectionItems];
    }, []);
};

export const useAgreementTableAddOther = (
    openModal: boolean,
    disabledByStatus: boolean,
    contractId: number,
    contractSeasons: ContractSeasonData[],
    contractConstructionTypes: ConstructionTypeData[],
) => {
    const { state }: { state: { selected: number } } | undefined = useLocation();

    const [isSubmittedDataTable, setIsSubmittedDataTable] = useState<boolean>(false);
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [queryWhere, setQueryWhere] = useState<Record<string, string[]>>();

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setQueryWhere((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [],
    );

    const filterVariables = useMemo(
        () => ({
            contractId,
        }),
        [contractId],
    );

    const variables = useMemo(
        () => ({
            contractId,
            where: getWhereVariables(queryWhere),
            order: { section: SortEnumType.Desc },
        }),
        [contractId, queryWhere],
    );

    const [agreementsOtherData, agreementsOtherStatus, agreementsOtherErrors, agreementsOtherRequest] = useRequest({
        requestId: 'getContractChangeOtherCost',
        query: uerContractChangeOtherCostRowsForIncluding,
        variables,
        withEffect: true,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'getContractChangeOtherCost',
        requestFn: agreementsOtherRequest,
        requestVariables: variables,
    });

    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                if (rowId) {
                    setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
                }
            },
        [],
    );

    const contractSeasonsList = useMemo(() => {
        const items = contractSeasons?.reduce<{ [key: string]: number }>(
            (acc, { id, year }: { id: number; year: number }) => {
                acc[id] = year;

                return acc;
            },
            {},
        );

        return { 0: 'Итого', ...items };
    }, [contractSeasons]);

    const CollapseKeys = R.tail(Object.keys(contractSeasonsList));

    const constructionTypesColumns = contractConstructionTypes?.map(({ shortName, id }: any) => ({
        headerName: shortName,
        marryChildren: true,
        children: Object.entries(contractSeasonsList).map(([key, season], index) => ({
            field: `${id}${key}`,
            headerName: `Cезон ${season}`,
            cellClass: 'textRight',
            spanHeaderHeight: true,
            valueFormatter: ({ data }: { data: ContractOtherCostItemData & { type?: string } }) => {
                if (!data || data.type) {
                    return '';
                }

                const keyAsNumber = Number(key);

                const value = data?.seasonLimits?.find(
                    (item: { constructionTypeId: number; seasonId: number }) =>
                        item.constructionTypeId === id && item.seasonId === keyAsNumber,
                )?.cost;

                return value ? formatNumberToRu(value, 2) : '';
            },
            hide: !!index,
            headerComponent: index ? undefined : UnitCostWithOtherHeader,
            headerComponentParams: index
                ? undefined
                : { collapseKeys: ['', ...CollapseKeys], id, displayName: 'Итого' },
            cellRenderer: index ? undefined : CostOtherCellRendererComponent,
            cellRendererParams: index
                ? undefined
                : {
                      constructionTypeId: id,
                      isExpanded: false,
                  },
        })),
    }));

    const columns = useMemo<(ColDef<ContractOtherCostItemData> | ColGroupDef<ContractOtherCostItemData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 48,
                resizable: false,
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: ContractOtherCostItemData & { type: string } }) => {
                    if (!data || data.type) {
                        return '';
                    }

                    return (
                        <Checkbox
                            checked={checkedIds[data.id]}
                            onChange={handleChangeCheckbox(data?.id)}
                            disabled={disabledByStatus}
                        />
                    );
                },
            },
            {
                headerName: 'Наименование прочих затрат',
                field: 'name',
                minWidth: 400,
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerOthersForIncludingToContractChangeNameFilter',
                    query: uerOthersForIncludingToContractChangeNameFilter,
                    childFieldAccessor: 'value',
                    whereVariables: 'value',
                    columns: [
                        {
                            headerName: '',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Способ расчета',
                field: 'calculatingType',
                width: 120,
                valueGetter: ({ data }) =>
                    (data?.calculatingType && TYPE_CONST[data.calculatingType]) ||
                    (data?.calculatingType && `${data?.calculatingTypeValue.toFixed(2)} %`),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerOthersForIncludingToContractChangeCalculatingTypeFilter',
                    query: uerOthersForIncludingToContractChangeCalculatingTypeFilter,
                    childFieldAccessor: 'value',
                    whereVariables: 'value',
                    columns: [
                        {
                            headerName: '',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            valueFormatter: ({ value }: { value: OtherCostCalculationTypeEnum }) =>
                                value === OtherCostCalculationTypeEnum.Calculation ? 'Расчет' : 'Процент',
                        },
                    ],
                    changeFilterHandler,
                },
            },
            ...constructionTypesColumns,
            {
                field: 'status',
                headerName: 'Статус записи',
                valueFormatter: ({ data }) => (data ? FactorStatusValue[data?.status] : ''),
                width: 132,
            },
            {
                headerName: 'Используется',
                minWidth: 260,
                flex: 1,
                children: [
                    {
                        headerName: 'НЗ',
                        field: 'isIncludedInWorkOrder',
                        valueGetter: ({ data }: { data: ContractOtherCostItemData & { type: string } }) =>
                            !data?.type ? yesOrNoRepresentation(data?.isIncludedInWorkOrder) : '',
                        minWidth: 90,
                        flex: 1,
                    },
                    {
                        headerName: 'Акт',
                        field: 'isIncludedInActKc2',
                        valueGetter: ({ data }: { data: ContractOtherCostItemData & { type: string } }) =>
                            !data?.type ? yesOrNoRepresentation(data?.isIncludedInActKc2) : '',
                        minWidth: 90,
                        flex: 1,
                    },
                    {
                        headerName: 'Изм. к НЗ',
                        field: 'isIncludedInWorkOrderChange',
                        valueGetter: ({ data }: { data: ContractOtherCostItemData & { type: string } }) =>
                            !data?.type ? yesOrNoRepresentation(data?.isIncludedInWorkOrderChange) : '',
                        minWidth: 90,
                        flex: 1,
                    },
                ],
            },
        ],
        [
            changeFilterHandler,
            checkedIds,
            constructionTypesColumns,
            disabledByStatus,
            filterVariables,
            handleChangeCheckbox,
        ],
    );

    const handleCloseErrorAgreementTable = useCallback(() => setIsSubmittedDataTable(false), []);

    useEffect(() => {
        if (state?.selected) {
            setCheckedIds((ids) => ({ ...ids, [state?.selected]: true }));
        }
    }, [handleChangeCheckbox, setCheckedIds, state?.selected]);

    useEffect(() => {
        setIsSubmittedDataTable(true);

        if (agreementsOtherStatus === RequestStatus.SUCCESS && !agreementsOtherErrors) {
            setIsSubmittedDataTable(false);
        }
    }, [agreementsOtherErrors, agreementsOtherStatus]);

    useEffect(() => {
        if (agreementsOtherData?.uerContractChangeOtherCostRowsForIncluding?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                getUpdateRowTable(agreementsOtherData.uerContractChangeOtherCostRowsForIncluding.items ?? []),
                getUpdateRowTable(agreementsOtherData.uerContractChangeOtherCostRowsForIncluding.items ?? []).length,
            );
        }
    }, [
        getRowParams,
        agreementsOtherData?.uerContractChangeOtherCostRowsForIncluding?.items,
        agreementsOtherData?.uerContractChangeOtherCostRowsForIncluding?.totalCount,
    ]);

    useEffect(() => {
        if (!openModal) {
            setQueryWhere({});
        }
    }, [openModal]);

    return {
        titleModal: TITLE,
        checkedIds,
        setCheckedIds,
        agreementsData: agreementsOtherData?.uerContractChangeOtherCostRowsForIncluding,
        agreementsStatus: agreementsOtherStatus,
        agreementsErrors: agreementsOtherErrors,
        finalColumns: columns,
        isSubmittedDataTable,
        handleCloseErrorAgreementTable,
        dataSource,
    };
};
